/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 标签选择浮层
 *   - 按 useCount desc 给出"最近常用" Top N
 *   - 剩余标签收在"全部标签"折叠区
 *   - 输入框包含匹配过滤 (不做拼音 — 一期不引入 pinyin 库)
 *   - 键盘导航: ↑↓ 切换焦点, Enter 选中, Esc 关闭
 *   - 已打过的标签 (current) 显示选中态
 *
 * 使用方式: 当成纯渲染组件用,父组件控制 visible 和 positioning。
 *   一般会被 antd Popover / 自定义浮层 包裹,见 PhotoCardTagOverlay。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CheckSquareOutlined,
  DownOutlined,
  SearchOutlined,
  StarOutlined
} from '@ant-design/icons'
import type { PhotoTag } from '../../photoTag/types'
import './index.scss'

export interface TagPickerPopoverProps {
  /** 全部可用标签 (调用方应已过滤掉 disabled=true 的) */
  tags: PhotoTag[]
  /** 当前照片已打的标签 id 集合,用于高亮选中态 */
  currentTagIds?: string[]
  /** 选中一个标签时回调 */
  onSelect: (tag: PhotoTag) => void
  /** 已选中态的标签被再次点击时回调 (一般是取消打标);未传则同样调 onSelect,由上层决定语义 */
  onUnselect?: (tag: PhotoTag) => void
  /** 顶部"批量"按钮回调;不传则不显示批量按钮 */
  onRequestBatch?: () => void
  /** 进入时是否自动 focus 搜索框,默认 true */
  autoFocus?: boolean
  /** 最近常用展示数量,默认 5 */
  topN?: number
  /** 标签为空时的提示文案 */
  emptyHint?: string
  /** 用户首次输入搜索词时回调 (一次搜索会话只触发一次), 调用方上报 dialog_click(search) */
  onSearch?: () => void
}

const DEFAULT_TOP_N = 5

const TagPickerPopover: React.FC<TagPickerPopoverProps> = ({
  tags,
  currentTagIds,
  onSelect,
  onUnselect,
  onRequestBatch,
  autoFocus = true,
  topN = DEFAULT_TOP_N,
  emptyHint = '当前团队还没有可用标签',
  onSearch
}) => {
  const [query, setQuery] = useState('')
  const [allExpanded, setAllExpanded] = useState(false)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  // 一次搜索会话 (空→非空) 只上报一次 search, 清空后允许下次再报
  const searchTrackedRef = useRef(false)

  const currentSet = useMemo(
    () => new Set(currentTagIds || []),
    [currentTagIds]
  )

  // 按 useCount desc 排序,作为整体顺序基础
  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => (b.useCount || 0) - (a.useCount || 0))
  }, [tags])

  // Top N (最近常用,按 useCount desc) + 剩余 (全部标签区)
  const topTags = sortedTags.slice(0, topN)
  const restTags = sortedTags.slice(topN)

  // 搜索结果 (跨 top+rest 一起匹配)
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return null
    return sortedTags.filter((t) => t.name.toLowerCase().includes(q))
  }, [query, sortedTags])

  // 可被键盘 focus 的标签 (搜索态 → 匹配结果;非搜索态 → top + 已展开则加 rest)
  const focusableTags = useMemo(() => {
    if (matched) return matched
    return allExpanded ? [...topTags, ...restTags] : topTags
  }, [matched, allExpanded, topTags, restTags])

  // 进入/搜索切换时把 focus 落到第一个
  useEffect(() => {
    if (focusableTags.length === 0) {
      setFocusedId(null)
    } else if (!focusedId || !focusableTags.some((t) => t.id === focusedId)) {
      setFocusedId(focusableTags[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched, allExpanded])

  // autoFocus
  useEffect(() => {
    if (autoFocus && searchRef.current) {
      // 延迟一帧,避免 Popover 动画中焦点被夺回
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [autoFocus])

  const handleSelect = useCallback(
    (tag: PhotoTag) => {
      if (currentSet.has(tag.id) && onUnselect) {
        onUnselect(tag)
      } else {
        onSelect(tag)
      }
    },
    [currentSet, onSelect, onUnselect]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (focusableTags.length === 0) return
    const idx = focusableTags.findIndex((t) => t.id === focusedId)
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      const next = (idx + 1) % focusableTags.length
      setFocusedId(focusableTags[next].id)
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const prev = idx <= 0 ? focusableTags.length - 1 : idx - 1
      setFocusedId(focusableTags[prev].id)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = idx >= 0 ? focusableTags[idx] : focusableTags[0]
      if (target) handleSelect(target)
    }
  }

  const renderTag = (tag: PhotoTag) => {
    const active = currentSet.has(tag.id)
    const focused = focusedId === tag.id
    return (
      <span
        key={tag.id}
        className={
          'tpp-tag' +
          (active ? ' tpp-tag--active' : '') +
          (focused ? ' tpp-tag--focused' : '')
        }
        onMouseEnter={() => setFocusedId(tag.id)}
        onClick={() => handleSelect(tag)}
      >
        {tag.name}
      </span>
    )
  }

  const showEmpty = sortedTags.length === 0
  const showNoMatch = matched && matched.length === 0

  return (
    <div className="tag-picker-popover" onKeyDown={handleKeyDown}>
      {/* 顶部:搜索 + 批量按钮 */}
      <div className="tpp-search-wrap">
        <input
          ref={searchRef}
          className="tpp-search"
          placeholder="搜索标签"
          value={query}
          onChange={(e) => {
            const v = e.target.value
            setQuery(v)
            if (v && !searchTrackedRef.current) {
              searchTrackedRef.current = true
              onSearch && onSearch()
            } else if (!v) {
              searchTrackedRef.current = false
            }
          }}
        />
        {onRequestBatch && (
          <button
            type="button"
            className="tpp-batch-btn"
            onClick={onRequestBatch}
            title="批量打标"
          >
            <CheckSquareOutlined /> 批量
          </button>
        )}
      </div>

      {/* 内容区 */}
      <div className="tpp-body">
        {showEmpty ? (
          <div className="tpp-empty">{emptyHint}</div>
        ) : showNoMatch ? (
          <div className="tpp-empty">无匹配标签</div>
        ) : matched ? (
          // 搜索态:直接列匹配结果
          <div className="tpp-section">
            <div className="tpp-section__label">
              <SearchOutlined /> 匹配 {matched.length} 个
            </div>
            <div className="tpp-tags">{matched.map(renderTag)}</div>
          </div>
        ) : (
          <>
            {/* 最近常用 */}
            <div className="tpp-section">
              <div className="tpp-section__label">
                <StarOutlined /> 最近常用
              </div>
              <div className="tpp-tags">{topTags.map(renderTag)}</div>
            </div>

            {/* 全部标签 (折叠) */}
            {restTags.length > 0 && (
              <>
                <div
                  className="tpp-divider"
                  onClick={() => setAllExpanded((v) => !v)}
                >
                  <span>全部标签 ({sortedTags.length})</span>
                  <DownOutlined
                    className={
                      'tpp-divider__chev' +
                      (allExpanded ? ' tpp-divider__chev--open' : '')
                    }
                  />
                </div>
                {allExpanded && (
                  <div className="tpp-section">
                    <div className="tpp-tags">{restTags.map(renderTag)}</div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TagPickerPopover
